"use client";
import { useCallback, useMemo, useState } from "react";

import {
  BetaColumnHeader,
  SortDescriptor,
  SortDirection,
  useBetaDataTableContext as useTableContext,
} from "@zapier/design-system";

import { SearchZapsOrder } from "@zapier/bff";
import {
  Table,
  useFolderAndZapsModalStates,
  useZapBulkDeleteState,
  ZapModals,
  type BulkSelectAction,
} from "@zapier/shared";
import { AssetTypes } from "@zapier/utils";
import {
  App,
  convertToAssetId,
  toZapDeleteMetadata,
  type HydratedZapAsset,
  useSplitTreatments,
  hasZapStorageMetadata,
  Zap,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import ZapRow from "./ZapRow";
import styles from "./ZapTable.module.css";
import { useShowEmptyZapTable } from "./useShowEmptyZapTable";
import { EmptyZapsState } from "./EmptyZapsState";
import { useBulkSelectItems } from "../../folder/FolderZapTable/useBulkSelectItems";
import { useEmitZapListSortEvent } from "@zapier/shared";

interface ZapTableProps {
  allowsSorting?: boolean;
  ariaLabel: string;
  bulkSelectActions?: BulkSelectAction[];
  isLoading?: boolean;
  isSearch?: boolean;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  order?: SearchZapsOrder;
  searchQuery?: string | null;
  shared?: string;
  virtualQuery?: string;
  zaps: Zap[];
  apps?: Record<string, App>;
  details?: string;
  selectedZapIds?: string[];
  showCheckboxes?: boolean;
  showReadOnlyZaps?: boolean;
  onCopyToAnotherAccount?: (asset: HydratedZapAsset) => void;
}

export function mergeZapAndBulkSelectItems(
  zapBulkSelectItems: BulkSelectAction[],
  bulkSelectActions?: BulkSelectAction[],
) {
  if (!bulkSelectActions || bulkSelectActions.length === 0) {
    return zapBulkSelectItems;
  }

  const shouldReplaceZapMoveAction = bulkSelectActions.some(
    (item) => item.id === "bulk-move-to-folder",
  );
  const zapItemsToMerge = shouldReplaceZapMoveAction
    ? zapBulkSelectItems.filter((item) => item.id !== "zap-move-to-folder")
    : zapBulkSelectItems;

  const merged = [...zapItemsToMerge, ...bulkSelectActions];

  // Keep "Move to trash" as the right-most bulk action, consistent with the
  // Canvas list, even though it originates from the zap-side items.
  const trashIndex = merged.findIndex((item) => item.id === "zap-delete");
  if (trashIndex === -1) {
    return merged;
  }
  const [trashAction] = merged.splice(trashIndex, 1);
  return [...merged, trashAction];
}

const ZapTable = ({
  allowsSorting = true,
  ariaLabel,
  bulkSelectActions,
  isLoading,
  isSearch,
  onSortChange,
  order,
  searchQuery,
  shared,
  virtualQuery,
  zaps,
  apps,
  details,
  selectedZapIds,
  showCheckboxes = false,
  showReadOnlyZaps = false,
  onCopyToAnotherAccount,
}: ZapTableProps) => {
  const { emit: emitZapListSort } = useEmitZapListSortEvent();
  const handleSortChange = useCallback(
    (desc: SortDescriptor) => {
      emitZapListSort({ column: String(desc.column) });
      onSortChange?.(desc);
    },
    [emitZapListSort, onSortChange],
  );
  const t = useTranslations("ZapTable");
  const tAssetTypes = useTranslations("asset-types");
  const { isLastRunEnabled } = useSplitTreatments();
  const { approvalType, setApprovalType, currentZapModal, setCurrentZapModal } =
    useFolderAndZapsModalStates();

  const { selectedRows } = useTableContext();

  const resolvedSelectedZapIds = selectedZapIds ?? selectedRows;

  const getZapDeleteMetadata = useCallback(
    (typedIds: string[]) => {
      const selectedTypedIds = new Set(typedIds);
      return zaps
        .filter((zap) =>
          selectedTypedIds.has(convertToAssetId(AssetTypes.Zap, zap.id)),
        )
        .map((zap) => toZapDeleteMetadata(zap));
    },
    [zaps],
  );
  const { onSelectionChange, getDisabledState } = useZapBulkDeleteState({
    getZapDeleteMetadata,
  });
  const zapDeleteState = useMemo(
    () => ({ onSelectionChange, getDisabledState }),
    [onSelectionChange, getDisabledState],
  );

  const zapBulkSelectItems = useBulkSelectItems({
    selectedZapIds: resolvedSelectedZapIds,
    setCurrentZapModal,
    isVirtualPage: !!virtualQuery,
    zapDeleteState,
  });

  const selectedZapsHaveStorage = resolvedSelectedZapIds?.every((id) =>
    zaps.some((zap) => zap.id === id && hasZapStorageMetadata(zap)),
  );
  const bulkSelectItems = useMemo(() => {
    return mergeZapAndBulkSelectItems(
      zapBulkSelectItems,
      bulkSelectActions,
    ).map((action) =>
      action.id === "zap-delete" && !selectedZapsHaveStorage
        ? { ...action, disabled: true }
        : action,
    );
  }, [bulkSelectActions, zapBulkSelectItems, selectedZapsHaveStorage]);

  const columns = useMemo((): BetaColumnHeader[] => {
    const cols: BetaColumnHeader[] = [
      {
        label: t("columns.status"),
        allowsSorting,
        sortKey: "enabled",
        className: styles.statusColumn,
      },
      {
        label: t("columns.apps"),
        className: styles.appsColumn,
      },
      {
        label: t("columns.location"),
        className: styles.locationColumn,
      },
      {
        label: t("columns.last-modified"),
        allowsSorting,
        sortKey: "updated_at",
        className: styles.lastModifiedColumn,
      },
    ];
    if (isLastRunEnabled) {
      cols.push({
        label: t("columns.last-run"),
        className: styles.lastRunColumn,
      });
    }
    return cols;
  }, [allowsSorting, isLastRunEnabled, t]);

  const sortDescriptor = {
    column: "updated_at",
    direction: "descending" as SortDirection,
  };

  if (order) {
    sortDescriptor.column = order.replace("-", "");
    sortDescriptor.direction = (
      order[0] === "-" ? "descending" : "ascending"
    ) as SortDirection;
  }

  const { emptyTableType, isCountLoading } = useShowEmptyZapTable({
    isSearch,
    order,
    shared,
    virtualQuery,
    isLoading,
    hasNoZaps: zaps.length === 0,
    sortDescriptor,
    details,
  });

  const [currentZapId, setCurrentZapId] = useState<string>();
  const setCurrentZap = useCallback(
    (zap: Zap | HydratedZapAsset | undefined) => {
      setCurrentZapId(zap?.id);
    },
    [],
  );
  const currentZap = zaps.find((zap) => zap.id === currentZapId);

  const currentZaps = useMemo(() => {
    if (resolvedSelectedZapIds && resolvedSelectedZapIds.length > 0) {
      const matched = zaps.filter((zap) =>
        resolvedSelectedZapIds.includes(zap.id),
      );
      if (matched.length > 0) {
        return matched;
      }
      // Fallback for cases where full zap data isn't in the current page (e.g. move-to-account)
      return resolvedSelectedZapIds.map(
        (zapId) =>
          ({
            id: zapId,
          }) as Zap,
      );
    }

    return currentZap ? [currentZap] : [];
  }, [currentZap, resolvedSelectedZapIds, zaps]);

  if (emptyTableType) {
    return <EmptyZapsState tableType={emptyTableType} />;
  }

  return (
    <>
      <Table
        allowSortByTitle={allowsSorting}
        ariaLabel={ariaLabel}
        bulkActionsAssetLabel={tAssetTypes("plural.zap")}
        bulkSelectActions={bulkSelectItems}
        columns={columns}
        isLoading={isLoading || isCountLoading}
        onSortChange={handleSortChange}
        showBulkSelectPaused={true}
        showCheckboxes={showCheckboxes}
        selectableEntities={showCheckboxes ? zaps : undefined}
        showOwner={true}
        sortDescriptor={sortDescriptor}
        showShareButton={false}
        className={styles.zapsTable}
      >
        {zaps.map((zap, index) => (
          <ZapRow
            key={index}
            zap={zap}
            allowSelection={showCheckboxes}
            showLastRun={isLastRunEnabled}
            setApprovalType={setApprovalType}
            setCurrentModal={setCurrentZapModal}
            setCurrentZap={setCurrentZap}
            apps={apps}
            searchQuery={searchQuery}
            showReadOnlyZaps={showReadOnlyZaps}
            onCopyToAnotherAccount={onCopyToAnotherAccount}
          />
        ))}
      </Table>
      <ZapModals
        approvalType={approvalType}
        currentModal={currentZapId && !currentZap ? undefined : currentZapModal}
        setApprovalType={setApprovalType}
        setCurrentModal={setCurrentZapModal}
        setCurrentZap={setCurrentZap}
        currentZaps={currentZaps}
        projectId={currentZap?.projectId}
      />
    </>
  );
};
export default ZapTable;
